// frontend/src/components/TopAppBar.tsx
// TopAppBar institucional — glass-navy, Lucide icons, safe-area-top.

import { Fragment, useState, useEffect } from 'react';
import { Menu, Cloud, CloudOff, LogOut, Check, RefreshCw, AlertTriangle, Download, ShieldCheck } from 'lucide-react';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { storage, type StoredRegistro } from '../services/storage';
import { SyncModal } from './SyncModal';
import { toast } from '../lib/toast';
import { AnimatePresence, m } from 'motion/react';
import { DUR, SPRING_POP } from '../lib/motion';

// Usuario guardado en localStorage por App.handleLoginSuccess (solo los campos
// que se incluyen en la exportación de pendientes).
interface UsuarioExportacion {
  id?: number;
  usuario?: string;
  compania_id?: number | null;
}

function leerUsuarioSesion(): UsuarioExportacion {
  try {
    const raw = localStorage.getItem('auth_user');
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const u = parsed as Record<string, unknown>;
    return {
      id: typeof u.id === 'number' ? u.id : undefined,
      usuario: typeof u.usuario === 'string' ? u.usuario : undefined,
      compania_id: typeof u.compania_id === 'number' || u.compania_id === null ? u.compania_id : undefined,
    };
  } catch {
    return {};
  }
}

// Registro de presentismo tal como sale en la exportación de pendientes: sin
// dispositivo_info ni latitud/longitud (datos del dispositivo / ubicación que
// soporte no necesita para reponer la marca).
interface RegistroExportado {
  local_id?: string;
  cadete_id: number;
  fecha: string;
  hora_local: string;
  estado: StoredRegistro['estado'];
  motivo?: string;
  rev?: number;
  pending_sync?: boolean;
  eliminado?: boolean;
  sync_rechazado?: boolean;
  sync_error?: string;
}

function exportarRegistro(r: StoredRegistro): RegistroExportado {
  return {
    local_id: r.local_id,
    cadete_id: r.cadete_id,
    fecha: r.fecha,
    hora_local: r.hora_local,
    estado: r.estado,
    motivo: r.motivo,
    rev: r.rev,
    pending_sync: r.pending_sync,
    eliminado: r.eliminado,
    sync_rechazado: r.sync_rechazado,
    sync_error: r.sync_error,
  };
}

// Operación de la cola de gestión en la exportación: solo tipo y fecha; para
// updateCadete, el id del cadete y QUÉ campos se tocaron (no sus valores).
interface OpColaExportada {
  tipo: string;
  created_at: string;
  cadete_id?: number;
  campos_modificados?: string[];
}

function exportarOpCola(op: { type: string; payload: unknown; created_at: string }): OpColaExportada {
  const salida: OpColaExportada = { tipo: op.type, created_at: op.created_at };
  if (op.type === 'updateCadete' && op.payload && typeof op.payload === 'object') {
    const payload = op.payload as Record<string, unknown>;
    if (typeof payload.id === 'number') salida.cadete_id = payload.id;
    if (payload.data && typeof payload.data === 'object') {
      salida.campos_modificados = Object.keys(payload.data as Record<string, unknown>);
    }
  }
  return salida;
}

// Descarga un archivo: navigator.share con archivos cuando existe (iOS en modo
// standalone no siempre respeta <a download>), con fallback al anchor.
async function descargarArchivo(nombre: string, blob: Blob): Promise<void> {
  const file = new File([blob], nombre, { type: blob.type });
  if (typeof navigator.share === 'function' && typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: nombre });
      return;
    } catch (err) {
      // Cancelado por el usuario: no caer al anchor (sería una segunda descarga)
      if (err instanceof DOMException && err.name === 'AbortError') return;
      console.warn('navigator.share falló, se usa descarga directa:', err);
    }
  }
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', nombre);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revocar en diferido: Safari/iOS puede no haber iniciado la descarga aún
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

interface TopAppBarProps {
  userName?: string;
  userRole?: string;
  isAdmin?: boolean;
  companiaSeleccionada?: string;
  onCompaniaChange?: (cia: string) => void;
  onLogout?: () => void;
  onCheckUpdates?: () => void;
  // Sólo se pasa para administradores: abre el panel /admin
  onOpenAdmin?: () => void;
}

export const COMPANIAS = [
  'Primera Compañía', 'Segunda Compañía', 'Tercera Compañía', 'Cuarta Compañía',
  'Quinta Compañía', 'Sexta Compañía', 'Séptima Compañía', 'Octava Compañía',
  'Novena Compañía', 'Décima Compañía',
  'Décimo Primera Compañía', 'Décimo Segunda Compañía', 'Décimo Tercera Compañía',
];

export function TopAppBar({
  userName,
  userRole,
  isAdmin,
  companiaSeleccionada,
  onCompaniaChange,
  onLogout,
  onCheckUpdates,
  onOpenAdmin,
}: TopAppBarProps) {
  const { isOnline, isSyncing, pendingCount, sincronizar, syncState, syncError, resetSyncState } =
    useOfflineSync();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // Registros que el servidor rechazó de forma permanente (no se reintentan)
  const [rechazados, setRechazados] = useState(0);

  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      try {
        const lista = await storage.getRegistrosRechazados();
        if (activo) setRechazados(Array.isArray(lista) ? lista.length : 0);
      } catch {
        // noop: la notificación es opcional
      }
    };
    void cargar();
    window.addEventListener('sgcc:sync-data-changed', cargar);
    return () => {
      activo = false;
      window.removeEventListener('sgcc:sync-data-changed', cargar);
    };
  }, []);

  // Exportación de emergencia (plan de migración §3.1 ítem 4): último recurso
  // para un dispositivo que no logra sincronizar nunca. Descarga las marcas
  // pendientes, la cola de gestión y los rechazados como JSON para soporte.
  const handleExportarPendientes = async () => {
    if (!confirm(
      'Esta exportación es SOLO para soporte técnico: genera un archivo JSON con las marcas pendientes de este dispositivo para enviarlo al equipo de SGCC si la sincronización no funciona.\n\n' +
      'El archivo contiene datos de cadetes (identificador, fecha, estado y motivo de cada marca): compartilo únicamente con el equipo de soporte de SGCC.\n\n' +
      'No reemplaza la sincronización: las marcas siguen pendientes acá hasta que se suban.\n\n¿Generar el archivo?'
    )) return;
    try {
      const [registrosPendientes, colaGestion, registrosRechazados] = await Promise.all([
        storage.getPendingRegistros(),
        storage.getQueue(),
        storage.getRegistrosRechazados(),
      ]);
      const usuario = leerUsuarioSesion();
      const ahora = new Date();
      const contenido = {
        exportado_en: ahora.toISOString(),
        usuario: { id: usuario.id ?? null, usuario: usuario.usuario ?? null, compania_id: usuario.compania_id ?? null },
        cliente_id: storage.generateClientId(),
        app_version: __APP_VERSION__,
        registros_pendientes: registrosPendientes.map(exportarRegistro),
        cola_gestion: colaGestion.map(exportarOpCola),
        registros_rechazados: registrosRechazados.map(exportarRegistro),
      };
      const nombreUsuario = (usuario.usuario || userName || 'usuario').replace(/[^a-zA-Z0-9_-]+/g, '_').toLowerCase();
      const fecha = ahora.toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(contenido, null, 2)], { type: 'application/json' });
      await descargarArchivo(`sgcc-pendientes-${nombreUsuario}-${fecha}.json`, blob);
    } catch (err) {
      console.error('Error al exportar pendientes:', err);
      toast.error('No se pudo generar el archivo de pendientes.');
    }
  };

  const handleDescartarRechazados = async () => {
    if (!confirm(`El servidor rechazó ${rechazados} registro(s) de presentismo (por ejemplo, cadete inexistente o sin permisos) y no se volverán a enviar.\n\n¿Descartarlos de este dispositivo?`)) return;
    try {
      await storage.descartarRegistrosRechazados();
      setRechazados(0);
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
    } catch (err) {
      console.error('Error al descartar registros rechazados:', err);
      toast.error('No se pudieron descartar los registros rechazados.');
    }
  };

  return (
    <header
      className="fixed top-0 w-full z-[1000] flex justify-between items-end px-4 pb-2"
      style={{
        backgroundColor: 'rgba(0, 30, 64, 0.88)',
        backdropFilter: 'blur(20px) saturate(1.8)',
        WebkitBackdropFilter: 'blur(20px) saturate(1.8)',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 1px 0 rgba(0,0,0,0.2)',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)',
        minHeight: 'calc(env(safe-area-inset-top, 0px) + 52px)',
      }}
    >
      {/* Left: menu + company */}
      <div className="flex items-center gap-1.5">
        <div className="relative">
          <button
            className="w-9 h-9 flex items-center justify-center rounded-[10px] transition-colors tap-active"
            style={{ color: 'white' }}
            aria-label="Menú"
            onClick={() => isAdmin && setMenuOpen((v) => !v)}
          >
            <Menu size={20} strokeWidth={2} />
          </button>

          <AnimatePresence>
          {isAdmin && menuOpen && (
            <Fragment key="menu">
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <m.div
                className="absolute left-0 mt-2 w-56 rounded-[16px] bg-white shadow-ios-lg z-50 p-2 max-h-[70vh] overflow-y-auto origin-top-left"
                style={{ border: '1px solid rgba(60,60,67,0.13)' }}
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, transition: { duration: DUR.rapida } }}
              >
                <p className="px-3 py-1.5 text-ios-caption2 font-bold text-[#8E8E93] uppercase tracking-widest">
                  Compañía
                </p>
                {COMPANIAS.map((cia) => (
                  <button
                    key={cia}
                    onClick={() => { onCompaniaChange?.(cia); setMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-[10px] text-ios-subhead font-medium transition-colors tap-active"
                    style={{
                      backgroundColor: companiaSeleccionada === cia ? '#d5e3ff' : 'transparent',
                      color: companiaSeleccionada === cia ? '#001e40' : '#43474f',
                    }}
                  >
                    {companiaSeleccionada === cia && (
                      <Check size={14} strokeWidth={2.5} style={{ color: '#001e40', flexShrink: 0 }} />
                    )}
                    <span className={companiaSeleccionada === cia ? 'font-semibold' : ''}>{cia}</span>
                  </button>
                ))}
              </m.div>
            </Fragment>
          )}
          </AnimatePresence>
        </div>

        <h1
          className="text-ios-headline font-bold tracking-tight uppercase"
          style={{ color: 'white' }}
        >
          {companiaSeleccionada || 'SGCC'}
        </h1>
      </div>

      {/* Right: sync + user */}
      <div className="flex items-center gap-1">
        {/* Sync button */}
        <button
          className="relative flex items-center gap-1 px-2 h-9 rounded-[10px] tap-active disabled:opacity-50"
          style={{ color: 'rgba(255,255,255,0.8)' }}
          onClick={() => { if (!isSyncing) sincronizar?.(); }}
          disabled={isSyncing}
          aria-label="Sincronizar"
        >
          {/* Mientras sincroniza, la nube gira; online/offline cambia con un fundido */}
          <AnimatePresence mode="popLayout" initial={false}>
            <m.span
              key={isOnline ? 'online' : 'offline'}
              className="flex"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={isSyncing
                ? { opacity: 1, scale: 1, rotate: 360, transition: { rotate: { repeat: Infinity, duration: 1.2, ease: 'linear' } } }
                : { opacity: 1, scale: 1, rotate: 0 }}
              exit={{ opacity: 0, scale: 0.7, transition: { duration: DUR.rapida } }}
            >
              {isOnline ? (
                <Cloud size={18} strokeWidth={1.8} style={{ color: pendingCount > 0 ? '#fbbf24' : '#4ade80' }} />
              ) : (
                <CloudOff size={18} strokeWidth={1.8} style={{ color: '#f87171' }} />
              )}
            </m.span>
          </AnimatePresence>
          <AnimatePresence>
          {pendingCount > 0 && (
            <m.span
              key="pendientes"
              initial={{ scale: 0 }}
              animate={{ scale: 1, transition: SPRING_POP }}
              exit={{ scale: 0, transition: { duration: DUR.rapida } }}
              className="absolute -top-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-black text-white"
              style={{ backgroundColor: '#f59e0b', border: '1.5px solid rgba(0,30,64,0.88)' }}
            >
              {/* El número hace "pop" cada vez que cambia */}
              <m.span key={pendingCount} initial={{ scale: 1.5 }} animate={{ scale: 1, transition: SPRING_POP }}>
                {pendingCount > 9 ? '9+' : pendingCount}
              </m.span>
            </m.span>
          )}
          </AnimatePresence>
        </button>

        {/* User avatar */}
        {userName && (
          <div className="relative">
            <button
              className="relative w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold cursor-pointer tap-active focus:outline-none"
              style={{ backgroundColor: '#fed65b', color: '#745c00' }}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {userName.charAt(0).toUpperCase()}
              {rechazados > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: '#f59e0b', border: '1.5px solid rgba(0,30,64,0.88)' }}
                  aria-label="Registros rechazados"
                />
              )}
            </button>

            <AnimatePresence>
            {dropdownOpen && (
              <Fragment key="menu">
                <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                <m.div
                  className="absolute right-0 mt-2 w-52 rounded-[16px] bg-white shadow-ios-lg z-50 p-2 origin-top-right"
                  style={{ border: '1px solid rgba(60,60,67,0.13)' }}
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97, transition: { duration: DUR.rapida } }}
                >
                  <div className="px-3 py-2.5">
                    <p className="text-ios-subhead font-semibold text-[#001e40] truncate">{userName}</p>
                    <p className="text-ios-caption1 text-[#8E8E93] uppercase tracking-wider mt-0.5">
                      {userRole || 'Operador'}
                    </p>
                  </div>

                  <div className="h-px mx-2 my-1" style={{ backgroundColor: 'rgba(60,60,67,0.13)' }} />

                  {rechazados > 0 && (
                    <>
                      <button
                        onClick={() => { setDropdownOpen(false); void handleDescartarRechazados(); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-[10px] text-ios-subhead font-medium text-[#b45309] hover:bg-amber-50 transition-colors tap-active"
                      >
                        <AlertTriangle size={16} strokeWidth={1.8} className="shrink-0" />
                        <span>{rechazados} rechazado(s) · Descartar</span>
                      </button>
                      <div className="h-px mx-2 my-1" style={{ backgroundColor: 'rgba(60,60,67,0.13)' }} />
                    </>
                  )}

                  {isAdmin && onOpenAdmin && (
                    <button
                      onClick={() => { setDropdownOpen(false); onOpenAdmin(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-[10px] text-ios-subhead font-medium text-[#001e40] hover:bg-slate-50 transition-colors tap-active"
                    >
                      <ShieldCheck size={16} strokeWidth={1.8} />
                      Administración
                    </button>
                  )}

                  <button
                    onClick={() => { setDropdownOpen(false); onCheckUpdates?.(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-[10px] text-ios-subhead font-medium text-[#001e40] hover:bg-slate-50 transition-colors tap-active"
                  >
                    <RefreshCw size={16} strokeWidth={1.8} />
                    Buscar Actualización
                  </button>

                  {pendingCount > 0 && (
                    <button
                      onClick={() => { setDropdownOpen(false); void handleExportarPendientes(); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-[10px] text-ios-subhead font-medium text-[#001e40] hover:bg-slate-50 transition-colors tap-active"
                    >
                      <Download size={16} strokeWidth={1.8} className="shrink-0" />
                      <span>Exportar pendientes (JSON)</span>
                    </button>
                  )}

                  <div className="h-px mx-2 my-1" style={{ backgroundColor: 'rgba(60,60,67,0.13)' }} />

                  <button
                    onClick={() => { setDropdownOpen(false); onLogout?.(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-[10px] text-ios-subhead font-medium text-[#FF3B30] transition-colors tap-active"
                  >
                    <LogOut size={16} strokeWidth={1.8} />
                    Cerrar Sesión
                  </button>
                </m.div>
              </Fragment>
            )}
            </AnimatePresence>
          </div>
        )}
      </div>

      <SyncModal
        state={syncState ?? 'idle'}
        error={syncError}
        onRetry={() => sincronizar?.()}
        onClose={() => resetSyncState?.()}
      />
    </header>
  );
}
